import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { setCredentials } from '../../store/authSlice';
import { apiPost } from '../../utils/api';
import { trackGAEvent } from '../../utils/analytics';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export const StravaCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const didRun = useRef(false);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code) {
      setStatus('error');
      setErrorMsg('No authorization code returned from Strava.');
      return;
    }

    if (didRun.current) return;
    didRun.current = true;

    const processOAuth = async () => {
      try {
        // If state is 'login', perform authenticating sign-in
        // Otherwise, if user is already authenticated, link the connection
        if (state === 'login' && !isAuthenticated) {
          console.log('Performing Strava login code exchange');
          const response = await apiPost('/api/auth/strava', { code });
          trackGAEvent('login', { method: 'strava' });
          dispatch(setCredentials({ token: response.accessToken }));
          setStatus('success');
          setTimeout(() => {
            navigate('/library');
          }, 1500);
        } else {
          // Linking existing account
          await apiPost('/api/integrations/link/strava', { code });
          trackGAEvent('connect_fitness_service', { service: 'strava' });
          setStatus('success');
          setTimeout(() => {
            navigate('/library');
          }, 1500);
        }
      } catch (err: any) {
        setStatus('error');
        setErrorMsg(err.message || 'Authentication failed. Please link your account first.');
      }
    };

    processOAuth();
  }, [searchParams, isAuthenticated, navigate, dispatch]);

  return (
    <div
      className="flex-center"
      style={{
        minHeight: '100vh',
        padding: '24px',
        background: 'radial-gradient(circle at top left, #1a1525 0%, #0d0b13 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated glowing mesh details */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          top: '-150px',
          left: '-150px',
          background: 'radial-gradient(circle, rgba(252, 82, 0, 0.08) 0%, rgba(252, 82, 0, 0) 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="glass-panel"
        style={{
          width: '100%',
          maxWidth: '440px',
          padding: '40px',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.04)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          background: 'rgba(21, 18, 30, 0.7)',
          backdropFilter: 'blur(20px)',
          textAlign: 'center',
          zIndex: 1,
        }}
      >
        <div style={{ marginBottom: '32px' }}>
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 800,
              background: 'linear-gradient(135deg, #fff 0%, #b3b1b9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              letterSpacing: '-0.02em',
            }}
          >
            Strava Connection
          </h2>
        </div>

        {status === 'loading' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <div
              className="spinner"
              style={{
                width: '40px',
                height: '40px',
                borderWidth: '3px',
                borderColor: 'var(--accent-primary) transparent transparent transparent',
              }}
            />
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Exchanging authorization code...</p>
          </div>
        )}

        {status === 'success' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <CheckCircle2 size={48} style={{ color: '#10b981' }} />
            <div>
              <p style={{ color: '#fff', fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>
                Successfully Connected!
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                Redirecting back to your track library...
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <AlertCircle size={48} style={{ color: '#ef4444' }} />
            <div>
              <p style={{ color: '#fff', fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Connection Failed</p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px', lineHeight: 1.5 }}>
                {errorMsg}
              </p>
              <button
                onClick={() => navigate(isAuthenticated ? '/library' : '/login')}
                className="btn btn-secondary"
                style={{ width: '100%', padding: '10px 16px', borderRadius: '10px', fontSize: '14px' }}
              >
                Go Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
